const DENSE_AXIS_MIN_CATEGORIES = 6;
const DENSE_AXIS_MIN_LABEL_LENGTH = 20;
const AXIS_LABEL_MAX_WIDTH = 100;
const DATA_ZOOM_FULL_RANGE = { start: 0, end: 100 };

/**
 * @param {Array<string | number | undefined>} labels
 * @returns {boolean}
 */
export function isDenseCategoryAxis(labels) {
    if (!labels?.length) return false;
    const maxLen = Math.max(...labels.map((l) => String(l).length), 0);
    return labels.length > DENSE_AXIS_MIN_CATEGORIES || maxLen > DENSE_AXIS_MIN_LABEL_LENGTH;
}

/**
 * @returns {Promise<void>}
 */
function waitForChartPaint() {
    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(resolve);
        });
    });
}

/**
 * Cattura PNG da istanza ECharts. Se dataZoom è attivo e non a range pieno,
 * espande temporaneamente a 100% prima della cattura.
 *
 * @param {import('echarts').ECharts | undefined | null} instance
 * @param {{ type?: string, pixelRatio?: number, backgroundColor?: string }} [options]
 * @returns {Promise<string | null>}
 */
export async function captureEchartsPng(instance, options = {}) {
    if (!instance) return null;

    const {
        type = "png",
        pixelRatio = 2,
        backgroundColor = "#fff",
    } = options;

    const option = instance.getOption();
    const dataZoomConfig = option.dataZoom;
    const hasDataZoom = Array.isArray(dataZoomConfig) && dataZoomConfig.length > 0;

    let previousZoom = null;
    if (hasDataZoom) {
        previousZoom = dataZoomConfig.map((dz) => ({
            start: dz.start ?? DATA_ZOOM_FULL_RANGE.start,
            end: dz.end ?? DATA_ZOOM_FULL_RANGE.end,
        }));
        const needsFullRange = previousZoom.some(
            (z) => z.start !== DATA_ZOOM_FULL_RANGE.start || z.end !== DATA_ZOOM_FULL_RANGE.end,
        );
        if (needsFullRange) {
            instance.dispatchAction({ type: "dataZoom", ...DATA_ZOOM_FULL_RANGE });
            await waitForChartPaint();
        } else {
            previousZoom = null;
        }
    }

    const url = instance.getDataURL({ type, pixelRatio, backgroundColor });

    if (previousZoom) {
        const restore = previousZoom[0];
        instance.dispatchAction({
            type: "dataZoom",
            start: restore.start,
            end: restore.end,
        });
    }

    return url;
}

/**
 * @param {Array<string | number | undefined>} labels
 * @param {boolean} isDense
 * @returns {number}
 */
function getGridBottom(labels, isDense) {
    if (!isDense) {
        return labels.length > 3 ? 70 : 50;
    }
    const maxLen = Math.max(...labels.map((l) => String(l).length), 0);
    return Math.min(120, 60 + Math.min(maxLen * 1.1, 55));
}

/**
 * Layout per assi categoria verticali (bar/line): label troncate, dataZoom e tooltip.
 *
 * @param {Array<string | number | undefined>} labels
 * @param {string} textColor
 */
export function buildVerticalCategoryAxisOptions(labels, textColor) {
    const isDense = isDenseCategoryAxis(labels);
    const rotate = labels.length > 3 ? (isDense ? 45 : 35) : 0;
    const gridBottom = getGridBottom(labels, isDense);

    const axisLabel = {
        color: textColor,
        interval: 0,
        rotate,
        ...(isDense
            ? {
                width: AXIS_LABEL_MAX_WIDTH,
                overflow: "truncate",
                ellipsis: "…",
            }
            : {}),
    };

    const grid = {
        left: "8%",
        right: "4%",
        top: "12%",
        bottom: isDense ? gridBottom + 36 : gridBottom,
        containLabel: false,
    };

    const dataZoom = isDense
        ? [
            {
                type: "slider",
                xAxisIndex: 0,
                ...DATA_ZOOM_FULL_RANGE,
                bottom: 8,
                height: 24,
                brushSelect: false,
            },
            {
                type: "inside",
                xAxisIndex: 0,
                ...DATA_ZOOM_FULL_RANGE,
            },
        ]
        : undefined;

    const tooltip = {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        textStyle: { color: textColor },
        formatter: (params) => {
            const point = Array.isArray(params) ? params[0] : params;
            if (!point) return "";
            return `${point.name}<br/><b>${point.value}</b>`;
        },
    };

    return { axisLabel, grid, dataZoom, tooltip, isDense };
}
